import { useEffect, useRef } from 'react';
import { AG_UI_STATE_PATCH_EVENT, type AgUiStatePatchEventDetail } from 'agentable-canvas/whiteboard';
import { putSiteCanvasState } from '../lib/site-media-api';
import { useCanvasSession } from '../contexts/CanvasSessionContext';

/**
 * Subscribes to AG-UI-shaped state patches from agentable tools and persists
 * canvas snapshots when the active site changes (Stage 09/10 bridge).
 */
export function useAgUiBridge(): void {
  const { session } = useCanvasSession();
  const siteId = session.siteId;
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<AgUiStatePatchEventDetail>).detail;
      if (!detail?.patches?.length || !siteId) return;

      const snapshotPatch = detail.patches.find((patch) => patch.path.startsWith('/snapshot'));
      if (!snapshotPatch) return;

      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        void putSiteCanvasState(siteId, {
          snapshot: snapshotPatch.value,
          updatedAt: new Date().toISOString(),
        }).catch((err: unknown) => {
          console.error('[useAgUiBridge] canvas state save failed', err);
        });
      }, 1500);
    };

    window.addEventListener(AG_UI_STATE_PATCH_EVENT, handler);
    return () => {
      window.removeEventListener(AG_UI_STATE_PATCH_EVENT, handler);
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [siteId]);
}
